import { config } from '@/config';
import { Config } from '@/utils/fetch';
import { FetchError } from '../FetchError/FetchError';
import { determinePathType } from '@/utils/determinePathType';
import { startsWithAny } from '@/utils/fp';
import { browserRedirect } from '../redirect';

const redirect = (url: string, routerPush: Config['routerPush']) => {
    if (routerPush) {
        void routerPush(url);
    } else {
        browserRedirect(url);
    }
};

const removeLocaleFromPathname = (pathname: string) => {
    if (pathname && startsWithAny(config.locales)(pathname.slice(1))) {
        return pathname.slice(6);
    }
    return pathname;
};

const redirectOnUnauthenticated = (status: number, routerPush: Config['routerPush']) => {
    if (status === 401) {
        let pathname = (typeof window !== 'undefined' && window?.location?.pathname) || '';
        pathname = removeLocaleFromPathname(pathname);

        const { isPrivatePath } = determinePathType(pathname);

        const logoutUrl =
            pathname && !pathname.includes('/login') ? `/logout?from=${pathname}` : '/logout';

        if (isPrivatePath) redirect(`${config.basePath}${logoutUrl}`, routerPush);
    }
};

const redirectOnAccessDenied = (
    status: number,
    routerPush: Config['routerPush'],
    errorPhraseIds?: number[] | null,
) => {
    const errorQueryParams = errorPhraseIds
        ? `?${errorPhraseIds.map((id) => `p=${id}`).join('&')}`
        : '';
    if (status === 403) {
        redirect(`${config.basePath}/403${errorQueryParams}`, routerPush);
    }
};

const redirectOnNotFound = (status: number, routerPush: Config['routerPush']) => {
    if (status === 404) {
        redirect(`${config.basePath}/404`, routerPush);
    }
};

export const redirectIfRequired = (
    status: number,
    configOptions: Config,
    errorPhraseIds?: number[] | null,
): void => {
    if (configOptions.disableRedirects) return;
    redirectOnUnauthenticated(status, configOptions.routerPush);
    redirectOnAccessDenied(status, configOptions.routerPush, errorPhraseIds);
    redirectOnNotFound(status, configOptions.routerPush);
};

export const throwCustomErrorIfProvided = (res: Response, configOptions: Config) => {
    const customError = configOptions?.customErrorPhraseId;
    if (!customError) return;
    const status = res?.status;
    const hasCorrectStatus = !!status && [401, 403, 404].includes(status);
    const errorPhraseId = customError[status as keyof typeof customError];
    if (!hasCorrectStatus || !errorPhraseId) return;
    const customErrorData = {
        errors: [
            {
                property: '',
                message: 'Custom error',
                helpUrl: '',
                phraseId: errorPhraseId,
            },
        ],
    };
    throw new FetchError(customErrorData, res);
};
